import { isDiscLetter } from '../../../constants/disc';
import type { DiscLetter } from '../../../constants/disc';

/**
 * 可以分享的亮點, read through the person's DISC type.
 *
 * Both IDEA pages end on four things the adviser can bring up (招募: the job;
 * Smart IDEA: the adviser's service). The four stay the same for everyone; what
 * changes with the type is which one leads and how it is worded:
 *  - D wants results and control — lead with what it gets them, briefly;
 *  - I wants people and recognition — lead with the team and the fun of it;
 *  - S wants safety and support — lead with who stands beside them;
 *  - C wants facts and structure — lead with what can be checked.
 *
 * Each point then reads its `-desc-<letter>` copy instead of the plain
 * `-desc`. Without a type on file, the plain order and copy stay.
 */

export type RecruitPointKey = 'time' | 'reward' | 'system' | 'mentor';
export type SellPointKey = 'sos' | 'review' | 'claims' | 'portfolio';

export const RECRUIT_POINT_ORDER: Record<DiscLetter, RecruitPointKey[]> = {
  D: ['reward', 'time', 'system', 'mentor'],
  I: ['time', 'mentor', 'reward', 'system'],
  S: ['mentor', 'system', 'time', 'reward'],
  C: ['reward', 'system', 'mentor', 'time']
};

export const SELL_POINT_ORDER: Record<DiscLetter, SellPointKey[]> = {
  D: ['portfolio', 'review', 'claims', 'sos'],
  I: ['review', 'sos', 'claims', 'portfolio'],
  S: ['claims', 'sos', 'review', 'portfolio'],
  C: ['portfolio', 'review', 'sos', 'claims']
};

/**
 * The points in the order this type cares about, each with the message id of
 * its description. `descId(key)` is the plain id; the type's variant appends
 * `-<letter>`.
 */
export const discOrderedPoints = <K extends string, P extends { key: K }>(
  points: readonly P[],
  orders: Record<DiscLetter, K[]>,
  letter: string | null | undefined,
  descId: (key: K) => string
): (P & { descId: string; lead: boolean })[] => {
  if (!isDiscLetter(letter)) return points.map(p => ({ ...p, descId: descId(p.key), lead: false }));
  const order = orders[letter];
  return [...points]
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
    .map((p, index) => ({ ...p, descId: `${descId(p.key)}-${letter}`, lead: index === 0 }));
};
